import React, { useEffect, useRef, useState, useCallback } from 'react';
import JsBarcode from 'jsbarcode';
import { getBOMForConfig } from '../config/bomTemplates';
import { calculateFabricationLabor } from '../utils/calculations';
import { LABOR_RATES } from '../config/bedConfigs';
import { saveOrder } from '../utils/storage';

// Label Print Component
function LabelPrintModal({ order, onClose }) {
  const labelBarcodeRef = useRef(null);
  const [labelCount, setLabelCount] = useState(1);
  const [labelSize, setLabelSize] = useState('2x4'); // 2x4 inches or 2x3
  
  useEffect(() => {
    if (labelBarcodeRef.current && order?.orderNumber) {
      JsBarcode(labelBarcodeRef.current, order.orderNumber, {
        format: 'CODE128',
        width: 1.5,
        height: 40,
        displayValue: true,
        fontSize: 12,
        margin: 5,
      });
    }
  }, [order]);

  const handlePrintLabels = useCallback(() => {
    const printWindow = window.open('', '_blank');
    const labelWidth = labelSize === '2x4' ? '4in' : '3in';
    const labelHeight = labelSize === '2x4' ? '2in' : '2in';
    
    const labelHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Labels - ${order.orderNumber}</title>
        <style>
          @page {
            size: ${labelWidth} ${labelHeight};
            margin: 0;
          }
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: Arial, sans-serif;
          }
          .label {
            width: ${labelWidth};
            height: ${labelHeight};
            padding: 8px;
            page-break-after: always;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            border: 1px dashed #ccc;
          }
          .label:last-child {
            page-break-after: auto;
          }
          .company {
            font-size: 9px;
            font-weight: bold;
            text-transform: uppercase;
            letter-spacing: 1px;
            text-align: center;
            color: #333;
          }
          .barcode-container {
            text-align: center;
            flex-grow: 1;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .barcode-container svg {
            max-width: 100%;
            height: auto;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
            font-size: 8px;
            border-top: 1px solid #eee;
            padding-top: 4px;
          }
          .info-item {
            text-align: center;
          }
          .info-label {
            font-size: 6px;
            color: #666;
            text-transform: uppercase;
          }
          .info-value {
            font-weight: bold;
            font-size: 9px;
          }
          @media print {
            .label {
              border: none;
            }
          }
        </style>
      </head>
      <body>
        ${Array(labelCount).fill().map(() => `
          <div class="label">
            <div class="company">Bliss Luxury Bunk Co.</div>
            <div class="barcode-container">
              <svg id="barcode"></svg>
            </div>
            <div class="info-row">
              <div class="info-item">
                <div class="info-label">Config</div>
                <div class="info-value">${order.bedConfig}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Wood</div>
                <div class="info-value">${order.woodType}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Finish</div>
                <div class="info-value">${order.finish}</div>
              </div>
            </div>
          </div>
        `).join('')}
        <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
        <script>
          document.querySelectorAll('#barcode').forEach(svg => {
            JsBarcode(svg, "${order.orderNumber}", {
              format: "CODE128",
              width: 1.5,
              height: 40,
              displayValue: true,
              fontSize: 12,
              margin: 5
            });
          });
          setTimeout(() => window.print(), 500);
        </script>
      </body>
      </html>
    `;
    
    printWindow.document.write(labelHTML);
    printWindow.document.close();
  }, [order, labelCount, labelSize]);

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-charcoal rounded-xl max-w-lg w-full border border-warm-gold/30">
        {/* Header */}
        <div className="p-6 border-b border-white/10">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-warm-gold text-xs uppercase tracking-wider mb-1">Print Labels</p>
              <h2 className="text-xl font-light text-white">Product Barcode Stickers</h2>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>
        
        {/* Preview */}
        <div className="p-6">
          <div className="bg-white rounded-lg p-4 mb-6">
            <div className="text-center mb-2">
              <span className="text-[9px] font-bold uppercase tracking-wider text-gray-800">
                Bliss Luxury Bunk Co.
              </span>
            </div>
            <div className="flex justify-center mb-2">
              <svg ref={labelBarcodeRef}></svg>
            </div>
            <div className="flex justify-between text-center border-t border-gray-200 pt-2">
              <div>
                <div className="text-[6px] text-gray-500 uppercase">Config</div>
                <div className="text-[9px] font-bold text-gray-800">{order.bedConfig}</div>
              </div>
              <div>
                <div className="text-[6px] text-gray-500 uppercase">Wood</div>
                <div className="text-[9px] font-bold text-gray-800">{order.woodType}</div>
              </div>
              <div>
                <div className="text-[6px] text-gray-500 uppercase">Finish</div>
                <div className="text-[9px] font-bold text-gray-800">{order.finish}</div>
              </div>
            </div>
          </div>
          
          {/* Settings */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="info-label mb-2">Label Size</label>
              <select
                value={labelSize}
                onChange={(e) => setLabelSize(e.target.value)}
                className="select-styled"
              >
                <option value="2x4">2" × 4" (Standard)</option>
                <option value="2x3">2" × 3" (Small)</option>
              </select>
            </div>
            <div>
              <label className="info-label mb-2">Number of Labels</label>
              <select
                value={labelCount}
                onChange={(e) => setLabelCount(parseInt(e.target.value))}
                className="select-styled"
              >
                {[1, 2, 3, 4, 5, 6, 8, 10, 12, 15, 20].map(n => (
                  <option key={n} value={n}>{n} label{n > 1 ? 's' : ''}</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Print Button */}
          <button
            onClick={handlePrintLabels}
            className="btn-gold w-full py-4 text-sm flex items-center justify-center gap-2"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 6 2 18 2 18 9"/>
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
              <rect x="6" y="14" width="12" height="8"/>
            </svg>
            Print {labelCount} Label{labelCount > 1 ? 's' : ''}
          </button>
          
          <p className="text-gray-custom text-xs text-center mt-4">
            Tip: Use label paper or print to a label printer for best results
          </p>
        </div>
      </div>
    </div>
  );
}

// Design Approval utilities
const APPROVAL_STORAGE_KEY = 'blissApprovalRequests';

function getDesignApproval(orderNumber) {
  try {
    const stored = localStorage.getItem(APPROVAL_STORAGE_KEY);
    if (!stored) return null;
    const approvals = JSON.parse(stored);
    // Find approval that matches this order (by order number or customer name)
    return approvals.find(a => 
      a.orderId === orderNumber || 
      a.orderNumber === orderNumber ||
      (a.orderId && a.orderId.includes(orderNumber))
    ) || null;
  } catch (error) {
    console.error('Error reading design approvals:', error);
    return null;
  }
}

function isApprovalValid(approval) {
  return approval && approval.status === 'approved' && approval.signature;
}

// Scrap inventory utilities
const SCRAP_STORAGE_KEY = 'blissScrapInventory';

function getScrapInventory() {
  try {
    const stored = localStorage.getItem(SCRAP_STORAGE_KEY);
    return stored ? JSON.parse(stored) : { plywood: [], lumber: [] };
  } catch (error) {
    console.error('Error reading scrap inventory:', error);
    return { plywood: [], lumber: [] };
  }
}

function saveScrapInventory(scrapInventory) {
  try {
    localStorage.setItem(SCRAP_STORAGE_KEY, JSON.stringify(scrapInventory));
  } catch (error) {
    console.error('Error saving scrap inventory:', error);
  }
}

function findMatchingScrap(part, scrapInventory, category) {
  if (!category || !scrapInventory[category]) return [];
  
  const scrapList = scrapInventory[category] || [];
  const availableScrap = scrapList.filter(s => !s.used && !s.allocatedTo);
  
  if (category === 'plywood') {
    const dims = part.dimensions.match(/(\d+(?:\.\d+)?)"/g);
    if (dims && dims.length >= 2) {
      const partLength = parseFloat(dims[0]);
      const partWidth = parseFloat(dims[1]);
      
      return availableScrap.filter(scrap => {
        if (!scrap.dimensions) return false;
        const scrapLength = scrap.dimensions.length || 0;
        const scrapWidth = scrap.dimensions.width || 0;
        return scrapLength >= (partLength - 1) && scrapWidth >= (partWidth - 1);
      });
    }
  }
  
  if (category === 'lumber') {
    const dims = part.dimensions.match(/(\d+(?:\.\d+)?)"/);
    if (dims) {
      const partLength = parseFloat(dims[0]);
      
      return availableScrap.filter(scrap => {
        if (!scrap.dimensions) return false;
        const scrapLength = scrap.dimensions.length || 0;
        return scrapLength >= (partLength - 1);
      });
    }
  }
  
  return [];
}

// Common plywood thicknesses for display
const THICKNESS_MAP = {
  0.25: '1/4"', 0.3125: '5/16"', 0.375: '3/8"', 0.46875: '15/32"',
  0.5: '1/2"', 0.59375: '19/32"', 0.625: '5/8"', 0.6875: '11/16"',
  0.71875: '23/32"', 0.75: '3/4"', 1.0: '1"', 1.5: '1-1/2"'
};

function formatThickness(thickness) {
  // Find closest match
  const entries = Object.entries(THICKNESS_MAP);
  const closest = entries.reduce((prev, curr) => 
    Math.abs(parseFloat(curr[0]) - thickness) < Math.abs(parseFloat(prev[0]) - thickness) ? curr : prev
  );
  return closest[1];
}

function formatScrapDimensions(scrap, category) {
  if (!scrap?.dimensions) return 'N/A';
  if (category === 'lumber') {
    return `${scrap.dimensions.length}" (${scrap.dimensions.dimension || 'N/A'})`;
  }
  const thickness = scrap.dimensions.thickness || 0.75;
  return `${scrap.dimensions.length}" × ${scrap.dimensions.width}" × ${formatThickness(thickness)}`;
}

function getAvailableScrapCount(scrapInventory) {
  const plywoodCount = (scrapInventory.plywood || []).filter(s => !s.used && !s.allocatedTo).length;
  const lumberCount = (scrapInventory.lumber || []).filter(s => !s.used && !s.allocatedTo).length;
  return plywoodCount + lumberCount;
}

// Find which parts a scrap piece can be used for based on dimensions
function getScrapRecommendations(scrap, partsList, category) {
  if (!scrap?.dimensions) return [];
  
  const recommendations = [];
  const scrapLength = scrap.dimensions.length || 0;
  const scrapWidth = scrap.dimensions.width || 0;
  
  partsList.forEach(part => {
    // Only check parts that match the category
    if (category === 'plywood' && part.material !== 'plywood') return;
    if (category === 'lumber' && part.material !== 'lumber') return;
    
    if (category === 'plywood') {
      // Parse dimensions like "77" x 10" x 11/16""
      const dims = part.dimensions.match(/(\d+(?:\.\d+)?)"?\s*x\s*(\d+(?:\.\d+)?)"?/i);
      if (dims && dims.length >= 3) {
        const partLength = parseFloat(dims[1]);
        const partWidth = parseFloat(dims[2]);
        
        // Check if scrap is big enough (with small tolerance)
        // Can use scrap in either orientation
        const fitsNormal = scrapLength >= (partLength - 0.5) && scrapWidth >= (partWidth - 0.5);
        const fitsRotated = scrapLength >= (partWidth - 0.5) && scrapWidth >= (partLength - 0.5);
        
        if (fitsNormal || fitsRotated) {
          recommendations.push({
            partName: part.name,
            partDimensions: `${partLength}" × ${partWidth}"`,
            fit: fitsNormal ? 'normal' : 'rotated',
          });
        }
      }
    } else if (category === 'lumber') {
      // Parse dimensions like "77" (2x4)"
      const dims = part.dimensions.match(/(\d+(?:\.\d+)?)"/);
      if (dims) {
        const partLength = parseFloat(dims[1]);
        
        if (scrapLength >= (partLength - 0.5)) {
          recommendations.push({
            partName: part.name,
            partDimensions: `${partLength}"`,
            fit: 'normal',
          });
        }
      }
    }
  });
  
  // Sort by part name
  return recommendations.sort((a, b) => a.partName.localeCompare(b.partName));
}

export default function FabricationWorkOrder({ order, onUpdate }) {
  const barcodeRef = useRef(null);
  const [localOrder, setLocalOrder] = useState(order);
  const [scrapInventory, setScrapInventory] = useState({ plywood: [], lumber: [] });
  const [scrapSubstitutions, setScrapSubstitutions] = useState({});
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [designApproval, setDesignApproval] = useState(null);
  const [approvalChecked, setApprovalChecked] = useState(false);

  useEffect(() => {
    setLocalOrder(order);
  }, [order]);

  // Check for design approval
  useEffect(() => {
    if (order?.orderNumber) {
      const approval = getDesignApproval(order.orderNumber);
      setDesignApproval(approval);
      setApprovalChecked(true);
    }
  }, [order]);

  useEffect(() => {
    if (barcodeRef.current && localOrder?.orderNumber) {
      JsBarcode(barcodeRef.current, localOrder.orderNumber, {
        format: 'CODE128',
        width: 2,
        height: 60,
        displayValue: true,
      });
    }
  }, [localOrder]);

  useEffect(() => {
    const scrap = getScrapInventory();
    setScrapInventory(scrap);
    
    if (order?.orderNumber) {
      const savedSubs = localStorage.getItem(`scrap_subs_${order.orderNumber}`);
      if (savedSubs) {
        setScrapSubstitutions(JSON.parse(savedSubs));
      }
    }
  }, [order]);

  if (!localOrder) {
    return (
      <div className="card p-12 text-center">
        <p className="text-gray-custom text-lg">No order selected</p>
      </div>
    );
  }

  // Check if design approval is required and valid
  const approvalRequired = true; // This could be a setting
  const hasValidApproval = isApprovalValid(designApproval);
  
  // Show approval gate if approval is required but not present/valid
  if (approvalRequired && approvalChecked && !hasValidApproval) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="card p-8 text-center border-2 border-yellow-500/50">
          {/* Warning Icon */}
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-yellow-500/20 flex items-center justify-center">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#eab308" strokeWidth="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </div>
          
          <h2 className="text-2xl font-light text-white mb-3">Design Approval Required</h2>
          
          <p className="text-gray-custom text-lg mb-6">
            A signed design approval is required before fabrication can begin.
          </p>
          
          {/* Order Info */}
          <div className="bg-charcoal-light rounded-lg p-4 mb-6">
            <div className="grid grid-cols-2 gap-4 text-left">
              <div>
                <span className="info-label">Order #</span>
                <span className="info-value text-warm-gold">{localOrder.orderNumber}</span>
              </div>
              <div>
                <span className="info-label">Customer</span>
                <span className="info-value">{localOrder.customerName || 'N/A'}</span>
              </div>
              <div>
                <span className="info-label">Configuration</span>
                <span className="info-value">{localOrder.bedConfig}</span>
              </div>
              <div>
                <span className="info-label">Due Date</span>
                <span className="info-value">{localOrder.dueDate || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Approval Status */}
          {designApproval ? (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center gap-2 text-yellow-400 mb-2">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
                <span className="font-medium">Approval Pending</span>
              </div>
              <p className="text-yellow-300/80 text-sm">
                Sent to {designApproval.customerName || 'customer'} on {new Date(designApproval.sentAt).toLocaleDateString()}
              </p>
              <p className="text-yellow-300/60 text-xs mt-1">
                Awaiting customer signature
              </p>
            </div>
          ) : (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center gap-2 text-red-400 mb-2">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
                <span className="font-medium">No Approval Found</span>
              </div>
              <p className="text-red-300/80 text-sm">
                No design approval request has been created for this order.
              </p>
            </div>
          )}

          {/* Instructions */}
          <div className="text-left bg-charcoal-light rounded-lg p-4">
            <h3 className="text-sm uppercase tracking-wider text-warm-gold mb-3">Next Steps</h3>
            <ol className="space-y-2 text-gray-light text-sm">
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-warm-gold/20 flex items-center justify-center text-xs text-warm-gold flex-shrink-0 mt-0.5">1</span>
                <span>Go to the <strong className="text-white">Sales Portal</strong> (localhost:5173/sales)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-warm-gold/20 flex items-center justify-center text-xs text-warm-gold flex-shrink-0 mt-0.5">2</span>
                <span>Select the order and open the <strong className="text-white">Design Approval</strong> tab</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-warm-gold/20 flex items-center justify-center text-xs text-warm-gold flex-shrink-0 mt-0.5">3</span>
                <span>Upload final design and send for <strong className="text-white">Customer Approval</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-warm-gold/20 flex items-center justify-center text-xs text-warm-gold flex-shrink-0 mt-0.5">4</span>
                <span>Once the customer signs, return here to generate the work order</span>
              </li>
            </ol>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <a 
              href="http://localhost:5173/sales" 
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold px-6 py-3 text-sm flex items-center justify-center gap-2"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                <polyline points="15 3 21 3 21 9"/>
                <line x1="10" y1="14" x2="21" y2="3"/>
              </svg>
              Open Sales Portal
            </a>
            <button
              onClick={() => {
                const approval = getDesignApproval(localOrder.orderNumber);
                setDesignApproval(approval);
              }}
              className="btn-gold-outline px-6 py-3 text-sm flex items-center justify-center gap-2"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 4 23 10 17 10"/>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
              </svg>
              Refresh Approval Status
            </button>
          </div>
        </div>
      </div>
    );
  }

  const bom = getBOMForConfig(localOrder.bedConfig);
  const fabLabor = calculateFabricationLabor(bom, localOrder.bedConfig);

  const partsList = [
    ...bom.plywood.map(item => ({
      name: item.name,
      qty: item.qty,
      dimensions: `${item.length}" x ${item.width}" x ${item.thickness}`,
      cutType: 'Rip Cut',
      laborPay: item.qty * LABOR_RATES.ripCut,
      material: 'plywood',
    })),
    ...bom.lumber.map(item => ({
      name: item.name,
      qty: item.qty,
      dimensions: `${item.length}" (${item.dimension})`,
      cutType: 'Cross Cut',
      laborPay: item.qty * LABOR_RATES.crossCut,
      material: 'lumber',
    })),
    {
      name: 'Edge Banding',
      qty: bom.cuts.edgeBanding,
      dimensions: `${bom.cuts.edgeBanding} LF`,
      cutType: 'Edge Banding',
      laborPay: bom.cuts.edgeBanding * LABOR_RATES.edgeBanding,
      material: null,
    },
    {
      name: 'Countersunk Holes',
      qty: bom.cuts.countersunkHoles,
      dimensions: '-',
      cutType: 'Drilling',
      laborPay: bom.cuts.countersunkHoles * LABOR_RATES.countersunkHole,
      material: null,
    },
    {
      name: 'Pocket Holes',
      qty: bom.cuts.pocketHoles,
      dimensions: '-',
      cutType: 'Drilling',
      laborPay: bom.cuts.pocketHoles * LABOR_RATES.pocketHole,
      material: null,
    },
  ];

  const partsWithScrap = partsList.map(part => {
    const substitution = scrapSubstitutions[part.name];
    const matchingScrap = substitution ? scrapInventory[substitution.category]?.find(s => s.id === substitution.scrapId) : null;
    
    return {
      ...part,
      scrapSubstitution: matchingScrap,
      scrapSubstitutionId: substitution?.scrapId,
      scrapCategory: substitution?.category,
      useScrap: substitution ? substitution.used : false,
    };
  });

  const handleScrapToggle = (partName, scrapId, category) => {
    const newSubs = { ...scrapSubstitutions };
    
    if (newSubs[partName] && newSubs[partName].scrapId === scrapId) {
      delete newSubs[partName];
    } else {
      newSubs[partName] = {
        scrapId,
        category,
        used: false,
      };
    }
    
    setScrapSubstitutions(newSubs);
    
    if (localOrder?.orderNumber) {
      localStorage.setItem(`scrap_subs_${localOrder.orderNumber}`, JSON.stringify(newSubs));
    }
  };

  const handleScrapUsed = (partName) => {
    const newSubs = { ...scrapSubstitutions };
    if (newSubs[partName]) {
      newSubs[partName].used = true;
      setScrapSubstitutions(newSubs);
      
      const sub = newSubs[partName];
      const updatedScrap = { ...scrapInventory };
      const scrapItem = updatedScrap[sub.category]?.find(s => s.id === sub.scrapId);
      if (scrapItem) {
        scrapItem.used = true;
        scrapItem.usedFor = localOrder.orderNumber;
        scrapItem.usedDate = new Date().toISOString();
        saveScrapInventory(updatedScrap);
        setScrapInventory(updatedScrap);
      }
      
      if (localOrder?.orderNumber) {
        localStorage.setItem(`scrap_subs_${localOrder.orderNumber}`, JSON.stringify(newSubs));
      }
    }
  };

  const handleStatusChange = (field, value) => {
    const updated = {
      ...localOrder,
      status: {
        ...localOrder.status,
        [field]: typeof value === 'boolean' ? value : value,
      },
    };
    setLocalOrder(updated);
    saveOrder(updated);
    if (onUpdate) onUpdate(updated);
  };

  const printDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric'
  });

  return (
    <div className="max-w-5xl mx-auto print:max-w-none print:mx-0">
      {/* Print-only company header */}
      <div className="hidden print:block print:mb-4 print:pb-4 print:border-b-2 print:border-black">
        <div className="print:flex print:justify-between print:items-start">
          <div>
            <h1 className="print:text-2xl print:font-bold print:m-0">BLISS LUXURY BUNK CO.</h1>
            <p className="print:text-sm print:text-gray-600 print:m-0">Santa Rosa Beach, Florida</p>
          </div>
          <div className="print:text-right">
            <p className="print:text-sm print:text-gray-600 print:m-0">Printed: {printDate}</p>
          </div>
        </div>
      </div>

      {/* Design Approval Badge */}
      {hasValidApproval && (
        <div className="mb-4 p-4 bg-green-500/10 border border-green-500/50 rounded-xl flex items-center justify-between no-print">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <div>
              <div className="text-green-400 font-medium">Design Approved ✓</div>
              <div className="text-green-300/70 text-sm">
                Signed by {designApproval.signedBy || designApproval.customerName} on {new Date(designApproval.signedAt).toLocaleDateString()}
              </div>
            </div>
          </div>
          <button
            onClick={() => {
              // Show signature in a modal or expand
              if (designApproval.signature) {
                const win = window.open('', '_blank', 'width=500,height=400');
                win.document.write(`
                  <html>
                  <head><title>Customer Signature - ${localOrder.orderNumber}</title></head>
                  <body style="font-family: Arial; padding: 20px; text-align: center;">
                    <h2>Design Approval Signature</h2>
                    <p>Order: ${localOrder.orderNumber}</p>
                    <p>Signed by: ${designApproval.signedBy || designApproval.customerName}</p>
                    <p>Date: ${new Date(designApproval.signedAt).toLocaleString()}</p>
                    <hr style="margin: 20px 0;">
                    <img src="${designApproval.signature}" style="max-width: 100%; border: 1px solid #ccc; padding: 10px;" alt="Signature"/>
                  </body>
                  </html>
                `);
              }
            }}
            className="text-sm text-green-400 hover:text-green-300 underline"
          >
            View Signature
          </button>
        </div>
      )}

      {/* Print-only approval confirmation */}
      <div className="hidden print:block print:mb-4 print:p-2 print:border print:border-gray-400">
        <div className="print:flex print:justify-between print:items-center">
          <span className="print:font-bold">✓ DESIGN APPROVED</span>
          {designApproval && (
            <span className="print:text-sm">
              Signed: {designApproval.signedBy || designApproval.customerName} | {new Date(designApproval.signedAt).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>

      {/* Screen header card */}
      <div className="card p-8 mb-6 print:p-0 print:mb-4 print:border-0">
        <h1 className="text-2xl font-light text-white mb-6 print:text-xl print:font-bold print:text-black print:mb-4 print:pb-2 print:border-b print:border-gray-400">
          FABRICATION WORK ORDER
        </h1>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-8 print:grid-cols-3 print:gap-4 print:mb-4">
          <div>
            <span className="info-label">Order #</span>
            <span className="info-value text-warm-gold">{localOrder.orderNumber}</span>
          </div>
          <div>
            <span className="info-label">Due Date</span>
            <span className="info-value">{localOrder.dueDate || 'N/A'}</span>
          </div>
          <div>
            <span className="info-label">Configuration</span>
            <span className="info-value">{localOrder.bedConfig}</span>
          </div>
          <div>
            <span className="info-label">Wood Type</span>
            <span className="info-value">{localOrder.woodType}</span>
          </div>
          <div>
            <span className="info-label">Finish</span>
            <span className="info-value">{localOrder.finish}</span>
          </div>
          <div>
            <span className="info-label">Customer</span>
            <span className="info-value">{localOrder.customerName || 'N/A'}</span>
          </div>
        </div>
        
        {/* Barcode */}
        <div className="flex justify-center py-4 bg-white rounded-lg print:py-2 print:rounded-none print:border print:border-gray-300">
          <svg ref={barcodeRef}></svg>
        </div>
      </div>

      {/* Scrap Bank Section - Always visible with management link */}
      <div className="mb-6 p-6 bg-green-500/10 border border-green-500/50 rounded-xl no-print">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-green-400 flex items-center gap-2">
            <span>♻️</span> SCRAP BANK
            <span className="text-sm font-normal text-green-300 ml-2">
              ({getAvailableScrapCount(scrapInventory)} pieces available)
            </span>
          </h2>
          <a 
            href="#" 
            onClick={(e) => { e.preventDefault(); window.location.reload(); }}
            className="text-sm text-green-400 hover:text-green-300 underline"
          >
            Manage Inventory →
          </a>
        </div>
        
        {getAvailableScrapCount(scrapInventory) === 0 ? (
          <div className="text-center py-6 text-green-300">
            <p className="mb-2">No scrap pieces in inventory</p>
            <p className="text-sm text-green-400/70">Go to Scrap Bank tab to add physical inventory</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-green-300 mb-4">
              Select scrap pieces below to substitute for new material cuts. Matching pieces are highlighted.
            </p>
            
            {/* Quick view of available scrap organized by type */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Plywood */}
              <div>
                <h3 className="font-semibold text-green-300 mb-2 text-sm uppercase tracking-wider flex items-center gap-2">
                  Plywood / Sheet Goods
                  <span className="text-xs font-normal text-green-400/70">
                    ({(scrapInventory.plywood || []).filter(s => !s.used && !s.allocatedTo).length})
                  </span>
                </h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {(scrapInventory.plywood || []).filter(s => !s.used && !s.allocatedTo).length === 0 ? (
                    <p className="text-sm text-green-400/50 italic">No plywood scrap available</p>
                  ) : (
                    (scrapInventory.plywood || []).filter(s => !s.used && !s.allocatedTo).map(scrap => {
                      const recommendations = getScrapRecommendations(scrap, partsList, 'plywood');
                      return (
                        <div key={scrap.id} className="bg-charcoal-light p-3 rounded-lg border border-green-500/30 hover:border-green-400 transition">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="font-medium text-white text-sm">{scrap.material || 'Plywood'}</div>
                            <div className="text-green-400 text-sm font-mono">
                              {scrap.dimensions?.length}" × {scrap.dimensions?.width}" × {formatThickness(scrap.dimensions?.thickness || 0.75)}
                            </div>
                            </div>
                            <div className="text-right">
                              {scrap.location && (
                                <div className="text-xs text-gray-light">{scrap.location}</div>
                              )}
                              <div className="text-xs text-green-400/70 font-mono">{scrap.id?.slice(-6)}</div>
                            </div>
                          </div>
                          
                          {/* Recommendations based on BOM analysis */}
                          {recommendations.length > 0 ? (
                            <div className="mt-2 pt-2 border-t border-green-500/20">
                              <div className="text-xs text-green-400 font-semibold mb-1">
                                ✓ Recommended for:
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {recommendations.map((rec, idx) => (
                                  <span 
                                    key={idx}
                                    className="inline-block px-2 py-0.5 bg-green-500/20 text-green-300 text-xs rounded"
                                    title={`Needs ${rec.partDimensions}`}
                                  >
                                    {rec.partName}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="mt-2 pt-2 border-t border-green-500/20">
                              <div className="text-xs text-yellow-500/80 italic">
                                ⚠ Too small for current order parts
                              </div>
                            </div>
                          )}
                          
                          {scrap.usableFor && (
                            <div className="text-xs text-gray-light mt-1">Note: {scrap.usableFor}</div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Lumber */}
              <div>
                <h3 className="font-semibold text-green-300 mb-2 text-sm uppercase tracking-wider flex items-center gap-2">
                  Lumber
                  <span className="text-xs font-normal text-green-400/70">
                    ({(scrapInventory.lumber || []).filter(s => !s.used && !s.allocatedTo).length})
                  </span>
                </h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {(scrapInventory.lumber || []).filter(s => !s.used && !s.allocatedTo).length === 0 ? (
                    <p className="text-sm text-green-400/50 italic">No lumber scrap available</p>
                  ) : (
                    (scrapInventory.lumber || []).filter(s => !s.used && !s.allocatedTo).map(scrap => {
                      const recommendations = getScrapRecommendations(scrap, partsList, 'lumber');
                      return (
                        <div key={scrap.id} className="bg-charcoal-light p-3 rounded-lg border border-green-500/30 hover:border-green-400 transition">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="font-medium text-white text-sm">{scrap.material || 'Lumber'}</div>
                              <div className="text-green-400 text-sm font-mono">
                                {scrap.dimensions?.length}" length ({scrap.dimensions?.dimension || 'N/A'})
                              </div>
                            </div>
                            <div className="text-right">
                              {scrap.location && (
                                <div className="text-xs text-gray-light">{scrap.location}</div>
                              )}
                              <div className="text-xs text-green-400/70 font-mono">{scrap.id?.slice(-6)}</div>
                            </div>
                          </div>
                          
                          {/* Recommendations based on BOM analysis */}
                          {recommendations.length > 0 ? (
                            <div className="mt-2 pt-2 border-t border-green-500/20">
                              <div className="text-xs text-green-400 font-semibold mb-1">
                                ✓ Recommended for:
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {recommendations.map((rec, idx) => (
                                  <span 
                                    key={idx}
                                    className="inline-block px-2 py-0.5 bg-green-500/20 text-green-300 text-xs rounded"
                                    title={`Needs ${rec.partDimensions}`}
                                  >
                                    {rec.partName}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="mt-2 pt-2 border-t border-green-500/20">
                              <div className="text-xs text-yellow-500/80 italic">
                                ⚠ Too small for current order parts
                              </div>
                            </div>
                          )}
                          
                          {scrap.usableFor && (
                            <div className="text-xs text-gray-light mt-1">Note: {scrap.usableFor}</div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Parts List */}
      <div className="card p-6 mb-6 print:p-0 print:mb-4 print:border-0">
        <h2 className="card-title mb-6">Parts List</h2>
        <div className="overflow-x-auto">
          <table className="table-styled">
            <thead>
              <tr>
                <th>Part Name</th>
                <th className="text-center" style={{ width: '60px' }}>Qty</th>
                <th>Dimensions</th>
                <th style={{ width: '100px' }}>Cut Type</th>
                <th className="no-print" style={{ width: '180px' }}>Use Scrap</th>
                <th className="text-right" style={{ width: '90px' }}>Labor Pay</th>
                <th className="text-center" style={{ width: '70px' }}>✓</th>
              </tr>
            </thead>
            <tbody>
              {partsWithScrap.map((part, idx) => {
                const category = part.material === 'plywood' ? 'plywood' : part.material === 'lumber' ? 'lumber' : null;
                const matchingScrap = category ? findMatchingScrap(part, scrapInventory, category) : [];
                const allAvailableScrap = category ? (scrapInventory[category] || []).filter(s => !s.used && !s.allocatedTo) : [];
                
                return (
                  <tr key={idx} className={part.scrapSubstitutionId ? 'bg-green-500/10' : ''}>
                    <td>
                      <span className="font-medium">{part.name}</span>
                      {part.scrapSubstitution && (
                        <div className="text-xs text-green-400 mt-1">
                          ♻️ Using: {part.scrapSubstitution.material || 'Scrap'} ({part.scrapSubstitution.id?.slice(-6)})
                        </div>
                      )}
                    </td>
                    <td className="text-center">
                      <span className="text-warm-gold font-semibold">{part.qty}</span>
                    </td>
                    <td className="text-gray-light">{part.dimensions}</td>
                    <td className="text-gray-light">{part.cutType}</td>
                    <td className="no-print">
                      {category && allAvailableScrap.length > 0 ? (
                        <div>
                          <select
                            value={part.scrapSubstitutionId || ''}
                            onChange={(e) => {
                              const scrapId = e.target.value;
                              if (scrapId) {
                                handleScrapToggle(part.name, scrapId, category);
                              } else if (part.scrapSubstitutionId) {
                                handleScrapToggle(part.name, part.scrapSubstitutionId, category);
                              }
                            }}
                            className={`w-full text-sm py-1 px-2 bg-charcoal-light border rounded text-white focus:outline-none ${
                              matchingScrap.length > 0 
                                ? 'border-green-500/50 focus:border-green-400' 
                                : 'border-white/10 focus:border-yellow-400'
                            }`}
                          >
                            <option value="">— Select scrap —</option>
                            {matchingScrap.length > 0 && (
                              <optgroup label="✓ FITS THIS PART">
                                {matchingScrap.map(scrap => (
                                  <option key={scrap.id} value={scrap.id}>
                                    ✓ {formatScrapDimensions(scrap, category)} • {scrap.material?.split(' ')[0] || 'Unknown'} ({scrap.id?.slice(-6)})
                                  </option>
                                ))}
                              </optgroup>
                            )}
                            {allAvailableScrap.filter(s => !matchingScrap.find(m => m.id === s.id)).length > 0 && (
                              <optgroup label="⚠ Too Small (Won't Fit)">
                                {allAvailableScrap.filter(s => !matchingScrap.find(m => m.id === s.id)).map(scrap => (
                                  <option key={scrap.id} value={scrap.id} disabled className="text-gray-500">
                                    {formatScrapDimensions(scrap, category)} • {scrap.material?.split(' ')[0] || 'Unknown'} ({scrap.id?.slice(-6)})
                                  </option>
                                ))}
                              </optgroup>
                            )}
                          </select>
                          {matchingScrap.length > 0 && !part.scrapSubstitutionId && (
                            <div className="text-xs text-green-400 mt-1">
                              ✓ {matchingScrap.length} scrap piece{matchingScrap.length > 1 ? 's' : ''} fit!
                            </div>
                          )}
                          {matchingScrap.length === 0 && (
                            <div className="text-xs text-yellow-500/70 mt-1">
                              No scrap large enough
                            </div>
                          )}
                        </div>
                      ) : category ? (
                        <span className="text-gray-custom text-xs italic">No scrap in inventory</span>
                      ) : (
                        <span className="text-gray-custom text-xs">N/A</span>
                      )}
                    </td>
                    <td className="text-right text-warm-gold font-medium">
                      {part.scrapSubstitutionId ? (
                        <span className="text-green-400">$0.00</span>
                      ) : (
                        `$${part.laborPay.toFixed(2)}`
                      )}
                    </td>
                    <td className="text-center">
                      {/* Print: show empty checkbox square */}
                      <span className="hidden print:inline-block print:w-4 print:h-4 print:border print:border-black"></span>
                      {/* Screen: interactive checkbox */}
                      <input
                        type="checkbox"
                        className="w-5 h-5 rounded border-2 border-white/20 bg-transparent checked:bg-warm-gold checked:border-warm-gold print:hidden"
                        style={{ accentColor: '#c9a959' }}
                        checked={localOrder.status?.[`${part.name}_complete`] || false}
                        onChange={(e) => handleStatusChange(`${part.name}_complete`, e.target.checked)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-warm-gold/10">
                <td colSpan="5" className="text-right font-semibold text-warm-gold">
                  Total Fabrication Labor:
                </td>
                <td className="text-right font-bold text-warm-gold text-lg">
                  ${fabLabor.toFixed(2)}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
        
        {/* Scrap Savings Summary */}
        {Object.keys(scrapSubstitutions).length > 0 && (
          <div className="mt-4 p-4 bg-green-500/10 border border-green-500/30 rounded-lg no-print">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-green-400">
                <span>♻️</span>
                <span className="font-medium">
                  {Object.keys(scrapSubstitutions).length} part(s) using scrap
                </span>
              </div>
              <button
                onClick={() => {
                  if (window.confirm('Mark all scrap substitutions as used?')) {
                    Object.keys(scrapSubstitutions).forEach(partName => {
                      handleScrapUsed(partName);
                    });
                  }
                }}
                className="text-sm px-3 py-1 bg-green-500/20 text-green-400 rounded hover:bg-green-500/30 transition"
              >
                Mark All Used
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Print-only Status Tracking Checklist */}
      <div className="hidden print:block print:mt-6">
        <h2 className="print:text-lg print:font-bold print:mb-3 print:pb-2 print:border-b print:border-gray-400">
          STATUS TRACKING
        </h2>
        <div className="print:grid print:grid-cols-2 print:gap-x-8 print:gap-y-2">
          {[
            'Cutting Complete',
            'Edge Banding Complete',
            'Holes Drilled Complete',
            'Ready for Paint',
            'Paint Complete',
            'QC Passed',
            'Packed / Ready to Ship',
            'Problem Flagged',
          ].map((label, idx) => (
            <div key={idx} className="print:flex print:items-center print:gap-2 print:py-1">
              <span className="print:inline-block print:w-4 print:h-4 print:border print:border-black print:flex-shrink-0"></span>
              <span className="print:text-sm">{label}</span>
            </div>
          ))}
        </div>
        <div className="print:mt-4 print:pt-2 print:border-t print:border-gray-300">
          <p className="print:text-sm print:font-semibold print:mb-1">Notes:</p>
          <div className="print:border print:border-gray-300 print:h-16"></div>
        </div>
      </div>

      {/* Screen-only Status Tracking */}
      <div className="card p-6 mb-6 no-print">
        <h2 className="card-title mb-6">Status Tracking</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { key: 'cuttingComplete', label: 'Cutting Complete' },
            { key: 'edgeBandingComplete', label: 'Edge Banding Complete' },
            { key: 'holesDrilledComplete', label: 'Holes Drilled Complete' },
            { key: 'readyForPaint', label: 'Ready for Paint' },
            { key: 'paintComplete', label: 'Paint Complete' },
            { key: 'qcPassed', label: 'QC Passed' },
            { key: 'packedReadyToShip', label: 'Packed / Ready to Ship' },
            { key: 'problemFlagged', label: 'Problem Flagged', isWarning: true },
          ].map(status => {
            const isChecked = localOrder.status?.[status.key] || false;
            return (
              <label 
                key={status.key} 
                className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                  isChecked 
                    ? status.isWarning 
                      ? 'bg-red-500/10 border-red-500' 
                      : 'bg-warm-gold/10 border-warm-gold' 
                    : 'bg-charcoal-light border-white/10 hover:border-warm-gold/50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={(e) => handleStatusChange(status.key, e.target.checked)}
                  className="w-5 h-5 rounded border-2 border-white/20 bg-transparent"
                  style={{ accentColor: status.isWarning ? '#e74c3c' : '#c9a959' }}
                />
                <span className={`text-sm ${isChecked && status.isWarning ? 'text-red-400' : 'text-white'}`}>
                  {status.label}
                </span>
              </label>
            );
          })}
        </div>
        
        {localOrder.status?.problemFlagged && (
          <div className="mt-6">
            <label className="info-label">Problem Notes</label>
            <textarea
              value={localOrder.status?.problemNotes || ''}
              onChange={(e) => handleStatusChange('problemNotes', e.target.value)}
              className="input-styled min-h-[100px] resize-y"
              placeholder="Describe the problem..."
            />
          </div>
        )}
      </div>

      {/* Print Buttons */}
      <div className="no-print flex flex-wrap gap-4">
        <button
          onClick={() => window.print()}
          className="btn-gold px-8 py-4 text-sm flex items-center gap-2"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="6 9 6 2 18 2 18 9"/>
            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
            <rect x="6" y="14" width="12" height="8"/>
          </svg>
          Print Work Order
        </button>
        
        <button
          onClick={() => setShowLabelModal(true)}
          className="btn-gold-outline px-8 py-4 text-sm flex items-center gap-2"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="5" width="18" height="14" rx="2"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
            <line x1="7" y1="15" x2="7" y2="15.01"/>
            <line x1="11" y1="15" x2="17" y2="15"/>
          </svg>
          Print Barcode Labels
        </button>
      </div>
      
      {/* Label Print Modal */}
      {showLabelModal && (
        <LabelPrintModal 
          order={localOrder} 
          onClose={() => setShowLabelModal(false)} 
        />
      )}
    </div>
  );
}
